"use client";

import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";

type ToastDetail = {
  kind: "error" | "success";
  message: string;
};

type ToastItem = ToastDetail & {
  id: number;
};

type ToastBridge = {
  handler?: (detail: ToastDetail) => void;
  queue: ToastDetail[];
};

const toastDuration = 3500;

function bridge() {
  const target = window as Window & { __avtToastBridge?: ToastBridge };
  if (!target.__avtToastBridge) target.__avtToastBridge = { queue: [] };
  return target.__avtToastBridge;
}

function publish(kind: ToastDetail["kind"], message: string) {
  const text = message.replace(/^[\s❌✅⚠]+/u, "").trim();
  if (!text) return;
  const detail = { kind, message: text };
  const channel = bridge();
  if (channel.handler) channel.handler(detail);
  else channel.queue.push(detail);
}

export const toast = {
  error(message: string) {
    publish("error", message);
  },
  success(message: string) {
    publish("success", message);
  },
};

export default function AppToaster() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    const channel = bridge();
    const shown: ToastDetail[] = [];

    function handler(detail: ToastDetail) {
      if (shown.some((item) => item.kind === detail.kind && item.message === detail.message)) return;
      shown.push(detail);
      const id = Date.now() + shown.length;
      setItems((current) => {
        if (current.some((item) => item.kind === detail.kind && item.message === detail.message)) return current;
        return [...current, { ...detail, id }];
      });
      window.setTimeout(() => {
        setItems((current) => current.filter((item) => item.id !== id));
      }, toastDuration);
    }

    channel.handler = handler;
    const queued = channel.queue.splice(0);
    queued.forEach(handler);

    return () => {
      if (channel.handler === handler) channel.handler = undefined;
      channel.queue.unshift(...shown);
    };
  }, []);

  return (
    <>
      <div className="avt-toast-stack" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={`avt-toast avt-toast-${item.kind}`} role="status">
            <span className="avt-toast-mark" aria-hidden="true">
              {item.kind === "success" ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3.5 8.2 6.4 11 12.5 4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 4.2v4.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <circle cx="8" cy="11.4" r="0.9" fill="currentColor" />
                </svg>
              )}
            </span>
            <span>{item.message}</span>
          </div>
        ))}
      </div>
      <Toaster
        position="top-right"
        containerStyle={{ zIndex: 110000 }}
        toastOptions={{
          duration: toastDuration,
          className: "avt-toast",
          success: {
            className: "avt-toast avt-toast-success",
            iconTheme: { primary: "#E65100", secondary: "#F9F6F0" },
          },
          error: {
            className: "avt-toast avt-toast-error",
            iconTheme: { primary: "#4A1118", secondary: "#F9F6F0" },
          },
        }}
      />
    </>
  );
}
